import { ExternalLink, Link2 } from 'lucide-react';
import { joinCss } from '@/shared/lib/css-utils';
import { Text } from '../text';
import css from './supplier-contacts-list.module.scss';

// `suppliers` genuinely carries TWO shapes in production, both holding a
// `crm.company` id: the Supplier edit tab writes `{ id, name, crmType: 1 }` and
// the subitem payload adapter writes `{ companyId, name }`. Reading only `id`
// silently drops every subitem-written supplier — and it fails as "no supplier"
// rather than as a bug.
//
// The stored `crmType` label is NOT a reliable discriminator: production holds
// thousands of rows tagged `crmType: 2` ("contact") whose id is in fact a live
// `crm.company`, so gating the CRM link on it hid real links. Company-ness is
// decided by whether `companies.resolveByIds` returns the id — that resolver
// already selects from `crm.company` and drops soft-deleted and out-of-org
// rows, so a returned row IS a reachable supplier account and a genuine
// contact id is simply never returned.
export type SupplierRef = {
  id: string;
  name: string | null;
  selected?: boolean;
  // The item's main supplier, set from Operations' supplier change dialog.
  primary?: boolean;
};

export type SupplierContactInfo = {
  email: string | null;
  phone: string | null;
  companyStatus: string | null;
};

export const resolveSupplierRefs = (value: unknown): SupplierRef[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const r = raw as Record<string, unknown>;
    const id =
      typeof r.id === 'string'
        ? r.id
        : typeof r.companyId === 'string'
          ? r.companyId
          : null;
    if (!id) return [];
    return [
      {
        id,
        name: typeof r.name === 'string' ? r.name : null,
        selected: r.selected === true,
        ...(r.primary === true && { primary: true }),
      },
    ];
  });
};

// Melhoria 7 §4: the destination is built from the immutable CRM id already on
// the wire — never by matching supplier name, email or website.
const crmRecordHref = (companyId: string) =>
  `/crm/accounts/${companyId}?pane=info`;

const copy = (value: string) => {
  navigator.clipboard?.writeText(value);
};

type Props = {
  suppliers: SupplierRef[];
  // `undefined` while the resolving query is in flight — the "not linked"
  // message must not flash before it settles.
  contactById: Map<string, SupplierContactInfo> | undefined;
  // Presentation-only gate: the CRM route runs its own server-side check.
  canOpenCrm: boolean;
};

/**
 * Read-only supplier list with click-to-copy contacts and the CRM link-state
 * line. Shared so the product preview and the planner item preview show the
 * same supplier information in the same shape.
 */
export const SupplierContactsList = ({
  suppliers,
  contactById,
  canOpenCrm,
}: Props) => {
  if (suppliers.length === 0) return <Text variant="muted">—</Text>;

  return (
    <ul className={css.supplierList}>
      {suppliers.map((supplier) => {
        const contact = contactById?.get(supplier.id);
        // Melhoria 7 §7 distinguishes three states. `resolveByIds` drops
        // soft-deleted and out-of-org rows, so a supplier the query did not
        // return is referenced-but-unreachable (or a real contact id, which
        // has no account route) and gets the unlinked message instead of an
        // action that would 404.
        const crmResolved = contactById !== undefined;
        const crmLinked = contact !== undefined;
        return (
          <li
            className={joinCss(
              css.supplierItem,
              supplier.selected ? css.supplierSelected : false
            )}
            key={supplier.id}
          >
            <span>{supplier.name || 'Unknown supplier'}</span>
            {crmResolved && crmLinked && (
              <span className={css.crmStatus}>
                <Link2 size={12} />
                Linked to supplier CRM
                {contact?.companyStatus &&
                  contact.companyStatus !== 'Active' &&
                  ` · ${contact.companyStatus}`}
              </span>
            )}
            {crmResolved && !crmLinked && (
              /* `as="p"` + the indent class, not a bare Text: Text renders a
                 <span>, which runs inline against the supplier name inside
                 this list item. */
              <Text
                as="p"
                className={css.crmUnlinked}
                size="xs"
                variant="muted"
              >
                Not linked to a supplier CRM record.
              </Text>
            )}
            {/* Melhoria 7 §2/§11: a real link, kept inside the supplier block
                so it reads as "opens the SUPPLIER record" and not "acts on this
                product". A plain anchor (not a router Link) so the modal and
                any unsaved itinerary work stay put in this tab; `noopener`
                stops the new tab reaching back into this one (§10). */}
            {crmResolved && crmLinked && canOpenCrm && (
              <a
                aria-label={`Open supplier CRM for ${
                  supplier.name || 'this supplier'
                } in a new tab`}
                className={css.crmLink}
                href={crmRecordHref(supplier.id)}
                rel="noopener noreferrer"
                target="_blank"
              >
                Open supplier CRM
                <ExternalLink size={12} />
              </a>
            )}
            {contact?.email && (
              <button
                className={css.contact}
                onClick={() => copy(contact.email ?? '')}
                title="Click to copy email"
                type="button"
              >
                <span className={css.contactDash}>| -</span> {contact.email}
              </button>
            )}
            {contact?.phone && (
              <button
                className={css.contact}
                onClick={() => copy(contact.phone ?? '')}
                title="Click to copy phone"
                type="button"
              >
                <span className={css.contactDash}>| -</span> {contact.phone}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};
